/** The same health numbers over a longer window, drawn as lines so a direction can be seen. */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { request } from '../api/client';
import type { Health } from '../api/types';
import { money, num } from '../components/format';
import { Failed, LineChart, Loading, Metric, PageHead, Panel } from '../components/ui';

export function Trends() {
  const [days, setDays] = useState(30);
  const { data, isPending, error } = useQuery({
    queryKey: ['health', days, null],
    queryFn: () => request<Health>(`/api/v1/admin/health?days=${days}`),
  });

  return (
    <>
      <PageHead
        eyebrow="Trends"
        title="Which way it is going"
        note="Daily figures across every customer. Cost is the marginal cost that day, as CostService priced it."
        actions={
          <select
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            aria-label="Window"
          >
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
          </select>
        }
      />
      {isPending ? <Loading what="Drawing" /> : null}
      {error ? <Failed error={error} fallback="Could not read trends." /> : null}
      {data ? <Body health={data} /> : null}
    </>
  );
}

function Body({ health }: { health: Health }) {
  const days = health.days;
  const busiest = days.reduce<(typeof days)[number] | undefined>(
    (best, d) => (best === undefined || d.active_users > best.active_users ? d : best),
    undefined,
  );
  const last7 = days.slice(-7);
  const prior7 = days.slice(-14, -7);
  const sum = (rows: typeof days, pick: (d: (typeof days)[number]) => number) =>
    rows.reduce((total, d) => total + pick(d), 0);
  const turnsOf = (d: (typeof days)[number]) => d.chat_turns + d.voice_turns + d.night_shift_runs;
  const week = sum(last7, turnsOf);
  const before = sum(prior7, turnsOf);
  const change = before ? Math.round(((week - before) / before) * 100) : null;

  return (
    <>
      <div className="cards">
        <Metric
          label="Turns, last 7 days"
          value={num(week)}
          note={
            change === null
              ? 'No prior week to compare'
              : `${change >= 0 ? '+' : ''}${change}% on the week before`
          }
        />
        <Metric
          label="Busiest day"
          value={busiest ? num(busiest.active_users) : '—'}
          note={busiest ? `people active on ${busiest.day}` : 'no data'}
        />
        <Metric
          label="Voice share, 7 days"
          value={week ? `${Math.round((sum(last7, (d) => d.voice_turns) / week) * 100)}%` : '—'}
          note="of turns spoken rather than typed"
        />
        <Metric
          label="Failure rate, 7 days"
          value={week ? `${Math.round((sum(last7, (d) => d.failed_turns) / week) * 100)}%` : '—'}
          note={`${num(sum(last7, (d) => d.failed_turns))} failed turns`}
          {...(sum(last7, (d) => d.failed_turns) ? { tone: 'bad' as const } : {})}
        />
      </div>

      <div className="panel-grid">
        <Panel eyebrow="People" title="Active users a day">
          <LineChart
            points={days.map((d) => ({ day: d.day, value: d.active_users }))}
            format={(v) => num(Math.round(v))}
          />
        </Panel>
        <Panel eyebrow="Customers" title="Active organisations a day">
          <LineChart
            points={days.map((d) => ({ day: d.day, value: d.active_organisations }))}
            format={(v) => num(Math.round(v))}
          />
        </Panel>
        <Panel eyebrow="Use" title="Turns a day">
          <LineChart
            points={days.map((d) => ({ day: d.day, value: turnsOf(d) }))}
            format={(v) => num(Math.round(v))}
          />
        </Panel>
        <Panel eyebrow="Voice" title="Voice turns a day">
          <LineChart
            points={days.map((d) => ({ day: d.day, value: d.voice_turns }))}
            format={(v) => num(Math.round(v))}
          />
        </Panel>
        <Panel eyebrow="Money" title="Marginal cost a day">
          <LineChart
            points={days.map((d) => ({ day: d.day, value: Number(d.cost_gbp) }))}
            format={(v) => money(v.toFixed(2))}
          />
        </Panel>
        <Panel eyebrow="Faults" title="Failed turns and server errors a day">
          <LineChart
            points={days.map((d) => ({ day: d.day, value: d.failed_turns + d.error_events }))}
            format={(v) => num(Math.round(v))}
          />
        </Panel>
      </div>
    </>
  );
}
